import { useContext, useEffect, useRef, useState } from 'react';
import { Col, ConfigProvider, Flex, Layout, Row } from 'antd';
import { DMS_TYPE, VEHICLE_STATUS } from '../../../utilities/constants';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import { API } from '../../../services';
import TripsList from './TripsList';
import TripTracking from './TripTracking';
import TripDetails from './TripDetails';
import useApiPolling from '../../../hooks/useApiPolling';
import PropTypes from 'prop-types';
import { getErrorMessage } from '../../../utilities/commonFunctions';

const { Content, Sider } = Layout;

const Monitoring = ({
  pageActiveKey,
  setLastUpdatedTime,
  selectedTripDetails,
  setSelectedTripDetails,
  activeTabKey,
  setActiveTabKey,
  setIsTripListLoading,
}) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [ongoingTripData, setOngoingTripData] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const onGoingTripApiTracker = useRef(null);
  const livePickupDropApiTracker = useRef(null);

  useEffect(() => {
    setIsTripListLoading?.(false);
  }, [setIsTripListLoading]);

  useEffect(() => {
    setSelectedTripDetails({});
    setOngoingTripData({});
  }, [pageActiveKey, activeTabKey]);

  const updateLastUpdatedTime = time => {
    setLastUpdatedTime(time);
  };

  useEffect(() => {
    setOngoingTripData({});
    onGoingTripApiTracker.current = { [selectedTripDetails?.trip_id]: true };
    livePickupDropApiTracker.current = { [selectedTripDetails?.trip_id]: true };
    getVehicleDetailsFromTripId(selectedTripDetails?.trip_id);
  }, [selectedTripDetails?.trip_id]);

  const getOngoingTripDetails = async tripId => {
    try {
      if (tripId) {
        const response = await API.getOngoingTripDetails(
          selectedTripDetails?.trip_id
        );
        if (response?.data) {
          const actualTimeline = response?.data?.timeline;
          const formattedTimeline = actualTimeline?.filter(
            data => data?.type !== DMS_TYPE.DISTRACTION
          );
          const ongoingDataWithoutDriverDistraction = {
            ...response?.data,
            timeline: formattedTimeline,
          };
          setOngoingTripData(ongoingDataWithoutDriverDistraction);
        }
        updateLastUpdatedTime(Date.now());
      }
    } catch (error) {
      if (
        selectedTripDetails?.trip_id === tripId &&
        onGoingTripApiTracker.current[tripId]
      )
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch ongoing trips data! Please try again after sometime.'
          )
        );
    } finally {
      onGoingTripApiTracker.current = { [selectedTripDetails?.trip_id]: false };
    }
  };

  const getVehicleDetailsFromTripId = async tripId => {
    setVehicleDetails(null);
    try {
      if (tripId) {
        const { data, message } = await API.getVehicleDetailsFromTrip({
          tripId,
        });
        if (data) {
          setVehicleDetails(data);
        }
        if (message) {
          addErrorMessage(message);
        }
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to get vehicle details! Please try again after sometime.'
        )
      );
    }
  };

  useEffect(() => {
    if (
      (pageActiveKey === 'logs' || activeTabKey === VEHICLE_STATUS.Inactive) &&
      selectedTripDetails?.trip_id
    ) {
      getOngoingTripDetails(selectedTripDetails?.trip_id);
    }
  }, [selectedTripDetails?.trip_id, pageActiveKey, activeTabKey]);

  useApiPolling(
    () => getOngoingTripDetails(selectedTripDetails?.trip_id),
    selectedTripDetails?.trip_id &&
      pageActiveKey === 'monitoring' &&
      activeTabKey === VEHICLE_STATUS.Active
      ? true
      : false,
    [selectedTripDetails?.trip_id, activeTabKey]
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginBottom: 0,
            titleMarginTop: 0,
          },
          Layout: {
            siderBg: 'var(--primary-fill-color, #1a1c1d)',
          },
          Divider: {
            colorSplit: 'var(--secondary-divider-color, #383B3E)',
          },
        },
        token: {
          fontSize: '0.875rem',
        },
      }}
    >
      <Layout className="full-height">
        <Sider
          minWidth={360}
          width="max-content"
          style={{
            height: '100%',
            boxShadow: 'rgba(0, 0, 0, 0.25) 0px 0px 8px 0px',
          }}
        >
          <TripsList
            selectedTripDetails={selectedTripDetails}
            setSelectedTripDetails={setSelectedTripDetails}
            activeTabKey={activeTabKey}
            setActiveTabKey={setActiveTabKey}
            pageActiveKey={pageActiveKey}
            setIsTripListLoading={setIsTripListLoading}
          />
        </Sider>
        <Content
          className="full-height"
          style={{
            marginLeft: '0.75rem',
            // paddingBottom: '16px',
          }}
        >
          <Flex gap={10} vertical className="full-height">
            <Row gutter={10} className="content-body">
              <Col
                xs={{ span: 24 }}
                xl={{ span: isFullScreen ? 24 : 15 }}
                className="full-height"
              >
                <Flex vertical gap={10} className="full-height">
                  <TripTracking
                    isFullScreen={isFullScreen}
                    onFullScreenToggle={() => setIsFullScreen(prev => !prev)}
                    selectedTrip={selectedTripDetails}
                    activeTabKey={activeTabKey}
                    pageActiveKey={pageActiveKey}
                    updateLastUpdatedTime={updateLastUpdatedTime}
                    showCustomFullScreenControl={true}
                  />
                </Flex>
              </Col>
              {!isFullScreen && (
                <Col xs={{ span: 24 }} xl={{ span: 9 }} className="full-height">
                  <TripDetails
                    ongoingTripData={ongoingTripData}
                    setOngoingTripData={setOngoingTripData}
                    onFullScreenToggle={() => setIsFullScreen(prev => !prev)}
                    vehicleDetails={vehicleDetails}
                    selectedTripDetails={selectedTripDetails}
                  />
                </Col>
              )}
            </Row>
          </Flex>
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

Monitoring.propTypes = {
  pageActiveKey: PropTypes.string.isRequired,
};

export default Monitoring;
